/**
 * useBatchOperations Hook
 *
 * Encapsulates the logic for:
 * - Batch report generation
 * - Bulk download of reports
 * - Bulk email sending
 * - Managing operation progress state
 *
 * Follows Single Responsibility Principle - only handles batch operations
 */

import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import JSZip from "jszip";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "@/hooks/useProject";
import { useReportQueue } from "@/hooks/useReportQueue";
import type { DownloadProgress } from "../store/types";

interface UseBatchOperationsOptions {
  /** Currently selected submission IDs */
  selectedIds: Set<number>;
  /** Selected template ID for operations */
  templateId: string;
  /** Callback to clear selection after operation */
  onOperationComplete?: () => void;
}

interface UseBatchOperationsResult {
  /** Generate reports for selected submissions */
  handleBatchGenerate: (notify: boolean) => Promise<void>;
  /** Download reports for selected submissions */
  handleBulkDownload: (downloadOnlyExisting?: boolean) => Promise<void>;
  /** Send email notifications for selected submissions */
  handleBulkEmail: () => Promise<void>;
  /** Whether batch generation is in progress */
  isGenerating: boolean;
  /** Whether bulk download is in progress */
  isDownloading: boolean;
  /** Whether bulk email is in progress */
  isSendingEmails: boolean;
  /** Download progress */
  downloadProgress: DownloadProgress;
  /** Reports data for selected submissions */
  selectedReportsData:
    | {
        reportsMap: Map<number, { id: number; file_name: string }>;
        withReport: number[];
        withoutReport: number[];
      }
    | undefined;
  /** Refetch selected reports data */
  refetchSelectedReports: () => void;
}

/**
 * Hook for managing batch operations on submissions
 *
 * @example
 * ```tsx
 * const {
 *   handleBatchGenerate,
 *   handleBulkDownload,
 *   isGenerating,
 *   selectedReportsData
 * } = useBatchOperations({
 *   selectedIds,
 *   templateId,
 *   onOperationComplete: clearSelection,
 * });
 *
 * // Generate reports
 * await handleBatchGenerate(true); // with notification
 *
 * // Download reports
 * await handleBulkDownload(false); // generate missing first
 * ```
 */
export function useBatchOperations({
  selectedIds,
  templateId,
  onOperationComplete,
}: UseBatchOperationsOptions): UseBatchOperationsResult {
  const { projectId } = useProject();
  const { enqueueBatchAsync, isEnqueuingBatch, sendNotificationAsync } =
    useReportQueue();

  // Local state for operation progress
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSendingEmails, setIsSendingEmails] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress>({
    current: 0,
    total: 0,
  });

  // Query to get report details for selected submissions with chosen template
  const {
    data: selectedReportsData,
    refetch: refetchSelectedReports,
  } = useQuery({
    queryKey: [
      "selected-reports",
      Array.from(selectedIds),
      templateId,
      projectId,
    ],
    queryFn: async () => {
      if (selectedIds.size === 0 || !templateId) {
        return {
          reportsMap: new Map<number, { id: number; file_name: string }>(),
          withReport: [] as number[],
          withoutReport: [] as number[],
        };
      }

      const { data: reports, error } = await supabase
        .from("generated_reports")
        .select("id, submission_id, file_name")
        .in("submission_id", Array.from(selectedIds))
        .eq("template_id", parseInt(templateId))
        .eq("project_id", projectId)
        .eq("status", "completed")
        .eq("is_outdated", false);

      if (error) throw error;

      const reportsMap = new Map<number, { id: number; file_name: string }>();
      (reports || []).forEach((r) => {
        reportsMap.set(r.submission_id, { id: r.id, file_name: r.file_name });
      });

      const withReport = Array.from(selectedIds).filter((id) =>
        reportsMap.has(id)
      );
      const withoutReport = Array.from(selectedIds).filter(
        (id) => !reportsMap.has(id)
      );

      return { reportsMap, withReport, withoutReport };
    },
    enabled: selectedIds.size > 0 && !!templateId && !!projectId,
    staleTime: 10 * 1000,
  });

  // Batch generate handler
  const handleBatchGenerate = useCallback(
    async (notify: boolean) => {
      if (!templateId || selectedIds.size === 0) {
        toast.error("Selecciona una plantilla");
        return;
      }

      try {
        await enqueueBatchAsync({
          submissionIds: Array.from(selectedIds),
          templateId: parseInt(templateId),
          priority: 2, // batch = lower priority
          notifyRecipient: notify,
        });

        onOperationComplete?.();
      } catch {
        // Error handled by hook
      }
    },
    [templateId, selectedIds, enqueueBatchAsync, onOperationComplete]
  );

  // Bulk download handler
  const handleBulkDownload = useCallback(
    async (downloadOnlyExisting = false) => {
      if (!templateId || !selectedReportsData) {
        toast.error("Selecciona un tipo de reporte");
        return;
      }

      const { reportsMap, withReport, withoutReport } = selectedReportsData;

      // If we need to generate missing reports first
      if (!downloadOnlyExisting && withoutReport.length > 0) {
        try {
          await enqueueBatchAsync({
            submissionIds: withoutReport,
            templateId: parseInt(templateId),
            priority: 1, // high priority for download
            notifyRecipient: false,
          });
          toast.info(
            `${withoutReport.length} reportes encolados para generación. Descarga los disponibles mientras se generan.`
          );
        } catch {
          toast.error("Error al encolar reportes faltantes");
        }
      }

      // Download existing reports
      if (withReport.length === 0) {
        toast.warning("No hay reportes disponibles para descargar");
        return;
      }

      setIsDownloading(true);
      setDownloadProgress({ current: 0, total: withReport.length });

      const zip = new JSZip();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const {
        data: { session },
      } = await supabase.auth.getSession();

      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < withReport.length; i++) {
        const submissionId = withReport[i];
        const reportInfo = reportsMap.get(submissionId);

        if (!reportInfo) continue;

        setDownloadProgress({ current: i + 1, total: withReport.length });

        try {
          const downloadUrl = `${supabaseUrl}/functions/v1/download-report/${reportInfo.id}?source=admin`;
          const response = await fetch(downloadUrl, {
            headers: session?.access_token
              ? { Authorization: `Bearer ${session.access_token}` }
              : {},
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          const blob = await response.blob();
          zip.file(reportInfo.file_name, blob);
          successCount++;
        } catch (error) {
          console.error(`Error downloading report ${reportInfo.id}:`, error);
          errorCount++;
        }
      }

      // Generate and download ZIP
      if (successCount > 0) {
        try {
          const zipBlob = await zip.generateAsync({ type: "blob" });
          const url = URL.createObjectURL(zipBlob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `reportes_${new Date().toISOString().split("T")[0]}.zip`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);

          toast.success(
            `${successCount} reportes descargados${errorCount > 0 ? `, ${errorCount} fallaron` : ""}`
          );
        } catch {
          toast.error("Error al crear el archivo ZIP");
        }
      } else {
        toast.error("No se pudo descargar ningún reporte");
      }

      setIsDownloading(false);
    },
    [templateId, selectedReportsData, enqueueBatchAsync]
  );

  // Bulk email handler
  const handleBulkEmail = useCallback(async () => {
    if (!templateId || !selectedReportsData) {
      toast.error("Selecciona un tipo de reporte");
      return;
    }

    const { reportsMap, withReport, withoutReport } = selectedReportsData;

    setIsSendingEmails(true);

    try {
      // For submissions without reports, generate first with notification enabled
      if (withoutReport.length > 0) {
        await enqueueBatchAsync({
          submissionIds: withoutReport,
          templateId: parseInt(templateId),
          priority: 1,
          notifyRecipient: true, // Will send email when report is ready
        });
        toast.success(
          `${withoutReport.length} reportes encolados para generación con notificación`
        );
      }

      // For submissions with reports, send notification directly
      if (withReport.length > 0) {
        let sentCount = 0;
        for (const submissionId of withReport) {
          const reportInfo = reportsMap.get(submissionId);
          if (reportInfo) {
            try {
              await sendNotificationAsync(reportInfo.id);
              sentCount++;
            } catch {
              // Individual errors logged but don't stop batch
            }
          }
        }
        if (sentCount > 0) {
          toast.success(`${sentCount} emails encolados para envío`);
        }
      }

      onOperationComplete?.();
    } catch {
      toast.error("Error al procesar envío de emails");
    } finally {
      setIsSendingEmails(false);
    }
  }, [
    templateId,
    selectedReportsData,
    enqueueBatchAsync,
    sendNotificationAsync,
    onOperationComplete,
  ]);

  return {
    handleBatchGenerate,
    handleBulkDownload,
    handleBulkEmail,
    isGenerating: isEnqueuingBatch,
    isDownloading,
    isSendingEmails,
    downloadProgress,
    selectedReportsData,
    refetchSelectedReports,
  };
}
